import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import * as React from "react";
import { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;

    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderDetailsBillingAddressStyles {
    titleText?: TypographyProps;
    addressDisplay?: AddressInfoDisplayStyles;
    wrapper?: InjectableCss;
}

export const billingAddressStyles: OrderDetailsBillingAddressStyles = {
    wrapper: {
        css: css`
            padding: 5px 24px;
            padding-bottom: 24px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 5px 14px;
                    `,
                    css`
                        padding: 5px 14px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 24px;
            font-size: 16px;
            padding-top: 10px;
            font-weight: 500;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        margin-bottom: 0px;
                        padding-top: 0;
                    `,
                    css`
                        font-size: 14px;
                        margin-bottom: 0px;
                        padding-top: 0;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
};

const styles = billingAddressStyles;

const OrderDetailsBillingAddress: React.FunctionComponent<Props> = ({ pageType, orderData }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;

    if (!order) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="orderDetailBillingInformation">
            <Typography {...styles.titleText}>{translate("BILL-TO")}</Typography>
            <AddressInfoDisplay
                companyName={order.btCompanyName}
                address1={order.btAddress1}
                address2={order.btAddress2}
                address3={order.btAddress3}
                address4={order.btAddress4}
                city={order.billToCity}
                state={order.billToState}
                postalCode={order.billToPostalCode}
                country={order.btCountry}
                extendedStyles={styles.addressDisplay}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDetailsBillingAddress),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
    },
};

export default widgetModule;
